/**
 * @author: subhasish.ghosh@zoom.us
 */

import {
  Box,
  CircularProgress,
  FormControl,
  InputAdornment,
  InputLabel,
  OutlinedInput,
} from "@mui/material";
import { Button } from "@zoom/react-ui";
import SearchIcon from "@mui/icons-material/Search";
import React, { useEffect, useMemo, useState } from "react";
import classNames from "classnames";
import "@components/styles.css";
import { DataGrid } from "@mui/x-data-grid";
import { columnsProductCatalogue } from "@options/dataGrid";
import { useQuery } from "@tanstack/react-query";
import CancellationTableTab from "@components/CancellationTableTab";
import {
  extractProductCatalogFailureReasonList,
  isProdEnvironment,
  isSelectedTableTabHistorical,
  isSelectedTableTabPending,
} from "@utils/utils";
import axiosInstance from "@utils/axiosInstance";
import NoRecords from "@components/NoRecords";
import CustomDataGridToolBar from "@components/CustomDataGridToolBar";
import { useSnackbar } from "notistack";
import UserInfoModal from "@components/UserInfoModal";
import Appbar from "@components/NavBar";
import ProductCatalogActionFailureReasonModal from "@components/ProductCatalogActionFailureReasonModal";

const ProductCatalogue = ({ userActionData, userInfoLoading }) => {
  const [isUserInfoModalOpen, setUserInfoModalOpen] = useState(false);

  const [isFailureReasonModalOpen, setFailureReasonModalOpen] = useState(false);

  const [failureReasonData, setFailureReasonData] = useState([]);

  const { enqueueSnackbar } = useSnackbar();

  const gridColumnState = useMemo(
    () => ({
      columns: {
        columnVisibilityModel: {
          ratePlanChargeId: true,
          accountId: true,
          status: true,
          createdDate: true,
          modifiedDate: true,
          approverName: true,
          approverEmail: true,
          actionTime: true,
        },
      },
    }),
    []
  );

  const [searchProductId, setSearchProductId] = useState("");

  const [selectedTableTab, setSelectedTableTab] = useState("");

  const [selectedRowIds, setSelectedRowIds] = useState([]);
  const [selectedRatePlanChargeIds, setSelectedRatePlanChargeIds] = useState(
    []
  );

  const [pendingData, setPendingData] = useState([]);
  const [historicalData, setHistoricalData] = useState([]);

  const [tDataLoading, setTDataLoading] = useState(true);
  const [hTDataLoading, setHTDataLoading] = useState(true);

  const handleSubmit = async () => {
    if (searchProductId?.length) {
      await axiosInstance
        .post(`/operationmonitor/api/v1/product-catalog`, {
          ratePlanChargeId: searchProductId,
        })
        .then(() =>
          enqueueSnackbar("Request submitted successfully.", {
            variant: "success",
          })
        )
        .catch(() =>
          enqueueSnackbar("Error submitting request.", { variant: "error" })
        )
        .finally(() => {
          tableDataRefetch();
          hTableDataRefetch();
        });
    } else {
      enqueueSnackbar("Please provide rate plan id.", {
        variant: "error",
      });
    }
  };

  const {
    data: tableData,
    isFetching: isTableDataFetching,
    refetch: tableDataRefetch,
  } = useQuery({
    queryKey: `product-catalogue-pending-data`,
    queryFn: async () => {
      setPendingData([]);
      return await axiosInstance.get(
        `/operationmonitor/api/v1/product-catalog`
      );
    },
    ...{
      enabled: isSelectedTableTabPending(selectedTableTab),
      refetchOnWindowFocus: false,
    },
  });

  const {
    data: hTableData,
    isFetching: isHTableDataFetching,
    refetch: hTableDataRefetch,
  } = useQuery({
    queryKey: `product-catalogue-historical-data`,
    queryFn: async () => {
      setHistoricalData([]);
      return await axiosInstance.get(
        `/operationmonitor/api/v1/product-catalog/audit`
      );
    },
    ...{
      enabled: isSelectedTableTabHistorical(selectedTableTab),
      refetchOnWindowFocus: false,
    },
  });

  const handleApproveOrReject = async (isApprove) => {
    if (selectedRowIds?.length) {
      await axiosInstance
        .post(`/operationmonitor/api/v1/product-catalog/action`, {
          ratePlanChargeId: selectedRatePlanChargeIds,
          status: isApprove ? "APPROVED" : "REJECTED",
          approverEmail: userActionData?.data?.data?.email,
        })
        .then((res) => {
          const response = res.data;
          const failuresList =
            extractProductCatalogFailureReasonList(response) || [];
          setFailureReasonData(failuresList);

          if (failuresList?.length) {
            enqueueSnackbar("Action completed with errors.", {
              variant: "warning",
            });
          } else {
            enqueueSnackbar("Action completed successfully.", {
              variant: "success",
            });
          }
        })
        .catch(() =>
          enqueueSnackbar("Error updating request.", { variant: "error" })
        )
        .finally(() => refetchFunc());
    } else {
      enqueueSnackbar("Please select pending approval records.", {
        variant: "error",
      });
    }
  };

  const refetchFunc = () => {
    setSelectedRowIds([]);
    // refresh table
    if (isSelectedTableTabPending(selectedTableTab)) {
      tableDataRefetch();
    } else {
      hTableDataRefetch();
    }
  };

  useEffect(() => {
    if (!isTableDataFetching && tableData?.data) {
      tableData.data?.forEach((item) => {
        item._id = item?.id;
        item.id = item?.id;

        const level1ApprovesNames = item?.levelApproves?.["1"]
          ?.filter((approver) => approver.name && approver.name !== "")
          ?.map((approver) => approver.name) || [""];
        const level2ApprovesNames = item?.levelApproves?.["2"]
          ?.filter((approver) => approver.name && approver.name !== "")
          ?.map((approver) => approver.name) || [""];

        item.level_1_approver = item?.l1Status?.name
          ? [item.l1Status.name]
          : level1ApprovesNames;
        item.level_1_status = item?.l1Status?.status || "";
        item.level_1_statusDate = item?.l1Status?.date || "";

        item.level_2_approver = item?.l2Status?.name
          ? [item.l2Status.name]
          : level2ApprovesNames;
        item.level_2_status = item?.l2Status?.status || "";
        item.level_2_statusDate = item?.l2Status?.date || "";

        if (item?.l2Status?.comments) {
          item.detailedInfo = item.l2Status.comments;
        } else if (item?.l1Status?.comments) {
          item.detailedInfo = item.l1Status.comments;
        } else {
          item.detailedInfo = "";
        }
      });
      setPendingData(tableData.data);
    } else {
      setPendingData([]);
    }
    setTDataLoading(false);
  }, [tableData, isTableDataFetching]);

  useEffect(() => {
    if (!isHTableDataFetching && hTableData?.data) {
      hTableData.data?.forEach((item) => {
        item._id = item?.id;
        item.id = item?.id;

        const level1ApprovesNames = item?.levelApproves?.["1"]
          ?.filter((approver) => approver.name && approver.name !== "")
          ?.map((approver) => approver.name) || [""];
        const level2ApprovesNames = item?.levelApproves?.["2"]
          ?.filter((approver) => approver.name && approver.name !== "")
          ?.map((approver) => approver.name) || [""];

        item.level_1_approver = item?.l1Status?.name
          ? [item.l1Status.name]
          : level1ApprovesNames;
        item.level_1_status = item?.l1Status?.status || "";
        item.level_1_statusDate = item?.l1Status?.date || "";

        item.level_2_approver = item?.l2Status?.name
          ? [item.l2Status.name]
          : level2ApprovesNames;
        item.level_2_status = item?.l2Status?.status || "";
        item.level_2_statusDate = item?.l2Status?.date || "";

        if (item?.l2Status?.comments) {
          item.detailedInfo = item.l2Status.comments;
        } else if (item?.l1Status?.comments) {
          item.detailedInfo = item.l1Status.comments;
        } else {
          item.detailedInfo = "";
        }
      });
      setHistoricalData(hTableData.data);
    } else {
      setHistoricalData([]);
    }
    setHTDataLoading(false);
  }, [hTableData, isHTableDataFetching]);

  useEffect(() => {
    const selected = pendingData
      ?.filter((data) => selectedRowIds?.includes(data.id))
      .map((data) => data.ratePlanChargeId);
    setSelectedRatePlanChargeIds(selected);
  }, [pendingData, selectedRowIds]);

  useEffect(() => {
    if (failureReasonData.length) {
      setFailureReasonModalOpen(true);
    }
  }, [failureReasonData]);

  return !isProdEnvironment() ? (
    <>
      <Box sx={{ display: "flex" }}>
        <Appbar
          appName="Product Catalogue"
          userName={userActionData?.data?.data?.name}
          isUserInfoLoading={userInfoLoading}
          setUserInfoModalOpen={setUserInfoModalOpen}
        />
        <Box
          component="main"
          sx={{ flexGrow: 1, margin: "80px 0 0 0", padding: "0" }}
        >
          <Box className="container-1">
            <div
              className={classNames(
                "responsive-row-1",
                "responsive-item-product-catalogue"
              )}
              style={{ padding: "10px" }}
            >
              <Box sx={{ display: "flex" }}>
                <Box
                  sx={{ display: "flex", flexWrap: "wrap" }}
                  className="search_input_container_product_catalogue"
                >
                  <FormControl
                    fullWidth
                    sx={{ m: 1 }}
                    className="aging-table-search-form"
                  >
                    <InputLabel htmlFor="outlined-adornment-amount">
                      Rate Plan ID
                    </InputLabel>
                    <OutlinedInput
                      startAdornment={
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      }
                      label="Rate Plan ID"
                      value={searchProductId}
                      id="searchbar-search-input"
                      className="search_input"
                      onChange={(e) => {
                        e.preventDefault();
                        setSearchProductId(e.target.value);
                      }}
                    />
                  </FormControl>
                </Box>
                <Box sx={{ alignContent: "center" }}>
                  <Button
                    id="product-catalogue-submit-btn-id"
                    type="primary"
                    size="large"
                    className="product-catalogue-submit-btn"
                    onClick={handleSubmit}
                    disabled={!searchProductId.length}
                  >
                    Submit
                  </Button>
                </Box>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Box>
                  <CancellationTableTab
                    selectedTableTab={selectedTableTab}
                    setSelectedTableTab={setSelectedTableTab}
                  />
                </Box>
                {isSelectedTableTabPending(selectedTableTab) ? (
                  <Box>
                    <Button
                      id="searchbar-approve-button"
                      type="primary"
                      size="large"
                      className="apprv_rjct_btn"
                      onClick={() => handleApproveOrReject(true)}
                      disabled={!selectedRowIds?.length > 0}
                      sx={{ marginRight: "15px" }}
                    >
                      Approve Cancellation(s)
                    </Button>
                    <Button
                      id="searchbar-reject-button"
                      size="large"
                      className="apprv_rjct_btn"
                      onClick={() => handleApproveOrReject(false)}
                      disabled={!selectedRowIds?.length > 0}
                      danger
                    >
                      Decline Cancellation(s)
                    </Button>
                  </Box>
                ) : (
                  <></>
                )}
              </Box>
              {!tDataLoading &&
              !hTDataLoading &&
              !isTableDataFetching &&
              !isHTableDataFetching ? (
                <div className="datagrid-parent-container">
                  {isSelectedTableTabPending(selectedTableTab) ? (
                    <DataGrid
                      rows={pendingData}
                      columns={columnsProductCatalogue}
                      columnHeaderHeight={95}
                      rowHeight={65}
                      initialState={gridColumnState}
                      pageSizeOptions={[10, 20, 50]}
                      pagination
                      paginationPageSize={10}
                      cacheBlockSize={10}
                      paginationMode="server"
                      sortingMode="server"
                      filterMode="server"
                      rowsLoadingMode="server"
                      checkboxSelection={true}
                      disableRowSelectionOnClick={true}
                      rowSelectionModel={selectedRowIds}
                      onRowSelectionModelChange={setSelectedRowIds}
                      keepNonExistentRowsSelected={true}
                      sx={{
                        "& .MuiDataGrid-columnHeaders": {
                          fontWeight: "bold",
                        },
                        height: "855px",
                      }}
                      slots={{
                        toolbar: CustomDataGridToolBar,
                        noRowsOverlay: () => (
                          <NoRecords refetchFunc={refetchFunc}>
                            <p>No approval(s) pending.</p>
                          </NoRecords>
                        ),
                      }}
                      autoHeight={false}
                      getCellClassName={(params) =>
                        `operations-datagrid-cell operations-datagrid-col-${params?.field}-${params?.id}`
                      }
                      getRowClassName={(params) =>
                        `operations-datagrid-row-${params?.id}`
                      }
                      slotProps={{
                        columnsManagement: {
                          getTogglableColumns: (params) =>
                            Object.keys(
                              gridColumnState?.columns?.columnVisibilityModel ||
                                {}
                            )?.filter(
                              (item) =>
                                ![
                                  "__check__",
                                  "accountNumber",
                                  "name",
                                ].includes(item)
                            ) || [],
                        },
                      }}
                      hideFooter={true}
                      hideFooterPagination={true}
                    />
                  ) : (
                    <DataGrid
                      rows={historicalData}
                      columns={columnsProductCatalogue}
                      columnHeaderHeight={90}
                      rowHeight={65}
                      initialState={gridColumnState}
                      pageSizeOptions={[10, 20, 50]}
                      pagination
                      paginationPageSize={10}
                      cacheBlockSize={10}
                      paginationMode="server"
                      sortingMode="server"
                      filterMode="server"
                      rowsLoadingMode="server"
                      checkboxSelection={false}
                      disableRowSelectionOnClick={true}
                      rowSelectionModel={selectedRowIds}
                      onRowSelectionModelChange={setSelectedRowIds}
                      keepNonExistentRowsSelected={true}
                      sx={{
                        "& .MuiDataGrid-columnHeaders": {
                          fontWeight: "bold",
                        },
                        height: "850px",
                      }}
                      slots={{
                        toolbar: CustomDataGridToolBar,
                        noRowsOverlay: () => (
                          <NoRecords refetchFunc={refetchFunc}>
                            <p>No approval(s) pending.</p>
                          </NoRecords>
                        ),
                        loadingOverlay: () => (
                          <Box
                            sx={{
                              height: "max-content",
                            }}
                          >
                            {[...Array(10)].map((_, key) => (
                              <Skeleton
                                animation="wave"
                                variant="rectangular"
                                sx={{ my: 4, mx: 1 }}
                                key={key}
                              />
                            ))}
                          </Box>
                        ),
                      }}
                      autoHeight={false}
                      getCellClassName={(params) =>
                        `operations-datagrid-cell operations-datagrid-col-${params?.field}-${params?.id}`
                      }
                      getRowClassName={(params) =>
                        `operations-datagrid-row-${params?.id}`
                      }
                      slotProps={{
                        columnsManagement: {
                          getTogglableColumns: (params) =>
                            Object.keys(
                              gridColumnState?.columns?.columnVisibilityModel ||
                                {}
                            )?.filter(
                              (item) =>
                                ![
                                  "__check__",
                                  "accountNumber",
                                  "name",
                                ].includes(item)
                            ) || [],
                        },
                      }}
                      hideFooter={true}
                      hideFooterPagination={true}
                    />
                  )}
                </div>
              ) : (
                <div className="tile-centralise-content">
                  <CircularProgress size="3rem" />
                </div>
              )}
            </div>
          </Box>
        </Box>
      </Box>
      <UserInfoModal
        isOpen={isUserInfoModalOpen}
        data={userActionData?.data?.data || {}}
        onClose={() => setUserInfoModalOpen(false)}
      />
      <ProductCatalogActionFailureReasonModal
        isOpen={isFailureReasonModalOpen}
        onClose={() => setFailureReasonModalOpen(false)}
        data={failureReasonData}
      />
    </>
  ) : (
    <></>
  );
};

export default ProductCatalogue;
